export function sheetBlank(colspan = 24) {
  return { type: 'blank', colspan };
}

export function sheetMeta(label, value, colspan = 2) {
  return {
    type: 'meta',
    label: String(label ?? ''),
    value: String(value ?? ''),
    colspan: Number(colspan) > 1 ? Number(colspan) : 2,
  };
}

export function sheetHeading(text, colspan = 24) {
  return { type: 'heading', text: String(text ?? ''), colspan };
}

export function sheetSection(text, colspan = 24) {
  return { type: 'section', text: String(text ?? ''), colspan };
}

export function sheetHeader(...cells) {
  return { type: 'header', cells: cells.map((cell) => String(cell ?? '')) };
}

export function sheetData(...cells) {
  return { type: 'data', cells: cells.map((cell) => String(cell ?? '')) };
}

function normalizeSheetCell(cell) {
  if (cell && typeof cell === 'object' && (cell.value != null || cell.html != null)) {
    return cell;
  }
  return String(cell ?? '');
}

export function sheetTotal(...cells) {
  return { type: 'total', cells: cells.map(normalizeSheetCell) };
}

function escapeHtml(text) {
  return String(text ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function rowColspan(row, fallback = 24) {
  return Number(row.colspan) > 0 ? Number(row.colspan) : fallback;
}

function renderCells(cells = [], cellTag = 'td', cellClass = '') {
  const classAttr = cellClass ? ` class="${cellClass}"` : '';
  return (cells || []).map((cell) => {
    if (cell && typeof cell === 'object') {
      const extraClass = [cellClass, cell.class].filter(Boolean).join(' ');
      const classPart = extraClass ? ` class="${extraClass}"` : '';
      const stylePart = cell.style ? ` style="${String(cell.style).replace(/"/g, '&quot;')}"` : '';
      const heightPart = cell.height != null ? ` height="${cell.height}"` : '';
      const valignPart = cell.valign ? ` valign="${cell.valign}"` : '';
      const attrs = `${classPart}${stylePart}${heightPart}${valignPart}`;
      if (cell.html != null) {
        return `<${cellTag}${attrs}>${cell.html}</${cellTag}>`;
      }
      if (cell.value != null) {
        return `<${cellTag}${attrs}>${escapeHtml(cell.value)}</${cellTag}>`;
      }
    }
    return `<${cellTag}${classAttr}>${escapeHtml(cell)}</${cellTag}>`;
  }).join('');
}

export function buildStyledSpreadsheetHtml(rows) {
  const parts = [
    '<!DOCTYPE html><html><head><meta charset="UTF-8">',
    '<style>',
    'table{border-collapse:collapse;font-family:Calibri,Arial,sans-serif;font-size:11pt;width:100%;}',
    'td,th{border:1px solid #d0d7de;padding:4px 8px;vertical-align:top;white-space:pre-wrap;}',
    'th{font-weight:bold;background:#dce6f1;text-align:left;}',
    '.heading{font-weight:bold;font-size:12pt;background:#f2f5fa;}',
    '.section{font-weight:bold;background:#eef2f7;}',
    '.meta-label{font-weight:bold;background:#fafafa;width:180px;text-align:left;mso-number-format:"\\@";}',
    '.meta-value{background:#ffffff;text-align:left;mso-number-format:"\\@";}',
    '.total td{font-weight:bold;background:#e8eef5;border-top:2px solid #1e3a5f;vertical-align:middle;}',
    '.total td.deductions-cell{background:#e8eef5;vertical-align:top;}',
    '.num{text-align:right;white-space:nowrap;}',
    '.net-pay-cell{text-align:right;font-weight:700;vertical-align:middle;color:#0f172a;}',
    '.employee-cell .emp-name{font-weight:600;color:#1e293b;}',
    '.employee-cell .emp-id{margin-top:2px;font-size:10px;color:#64748b;}',
    '.wrap{white-space:pre-wrap;}',
    '.deductions-cell{min-width:200px;height:100%;vertical-align:top;padding:4px 8px;}',
    'table.deduction-wrap{width:100%;height:100%;border-collapse:collapse;border:none;background:transparent;}',
    'table.deduction-wrap td{border:none;padding:0;background:transparent;}',
    'tr.deduction-wrap-spacer td{height:100%;font-size:0;line-height:0;background:transparent;}',
    'td.deduction-wrap-top{vertical-align:top;background:transparent;}',
    'td.deduction-wrap-bottom{vertical-align:bottom;background:transparent;}',
    'table.deduction-table{width:100%;border-collapse:collapse;border:none;table-layout:fixed;background:transparent;}',
    'table.deduction-table td{border:none;padding:2px 0;vertical-align:middle;background:transparent;}',
    'table.deduction-table tr+tr td{border-top:1px solid #eef2f7;}',
    'table.deduction-table tr.amount-pad-row td{border-top:none;color:transparent;}',
    'table.deduction-table tr.amount-total-row td{border-top:1px dashed #94a3b8;padding-top:5px;font-weight:600;color:#334155;}',
    'table.deduction-table td.deduction-label,table.deduction-table td.deduction-total-label{color:#1e3a5f;padding-right:6px;text-align:left;}',
    'table.deduction-table td.deduction-amt,table.deduction-table td.deduction-total-val{font-weight:600;color:#9a3412;text-align:right;width:5.75rem;white-space:nowrap;font-variant-numeric:tabular-nums;font-family:"Courier New",Courier,monospace;padding-left:4px;padding-right:0;}',
    'table.deduction-table td.deduction-total-val{color:#1e293b;font-weight:700;}',
    'table.deduction-table tr.deduction-section-title-row td{border-top:none;padding-top:8px;}',
    '.deduction-section-title{font-weight:700;font-style:italic;color:#1e3a5f;text-align:center;text-transform:none;letter-spacing:0;font-size:11px;}',
    'table.deduction-table tr.amount-subtotal-row td{font-weight:700;color:#1e293b;border-top:1px solid #94a3b8;}',
    '</style></head><body><table>',
  ];

  (rows || []).forEach((row) => {
    switch (row.type) {
      case 'blank':
        parts.push(`<tr><td colspan="${rowColspan(row)}">&nbsp;</td></tr>`);
        break;
      case 'heading':
        parts.push(`<tr><td class="heading" colspan="${rowColspan(row)}">${escapeHtml(row.text)}</td></tr>`);
        break;
      case 'section':
        parts.push(`<tr><td class="section" colspan="${rowColspan(row)}">${escapeHtml(row.text)}</td></tr>`);
        break;
      case 'meta': {
        const metaColspan = rowColspan(row, 2);
        const valueColspan = Math.max(metaColspan - 1, 1);
        parts.push(
          `<tr><td class="meta-label" style="mso-number-format:'\\@';text-align:left;">${escapeHtml(row.label)}</td>`
          + `<td class="meta-value" colspan="${valueColspan}" style="mso-number-format:'\\@';text-align:left;">${escapeHtml(row.value)}</td></tr>`
        );
        break;
      }
      case 'header':
        parts.push(`<tr>${renderCells(row.cells, 'th')}</tr>`);
        break;
      case 'total':
        parts.push(`<tr class="total">${renderCells(row.cells, 'td')}</tr>`);
        break;
      case 'data':
      default:
        parts.push(`<tr>${renderCells(row.cells, 'td')}</tr>`);
        break;
    }
  });

  parts.push('</table></body></html>');
  return parts.join('');
}

export function ensureExcelFilename(filename) {
  if (!filename) return 'export.xls';
  if (/\.(xls|xlsx)$/i.test(filename)) return filename;
  return filename.replace(/\.csv$/i, '.xls');
}

export function downloadStyledSpreadsheet(rows, filename) {
  const html = buildStyledSpreadsheetHtml(rows);
  const blob = new Blob([`\uFEFF${html}`], { type: 'application/vnd.ms-excel;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', ensureExcelFilename(filename));
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
